import { useEffect, useState } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { MessageCircle, Loader2, CheckCircle2, LogOut, Smartphone } from 'lucide-react'
import toast from 'react-hot-toast'
import { api } from '@/lib/api'
import { useFilial } from '@/lib/filial'

function numeroDoMe(me: any): string {
  const id: string = me?.id || me?.jid || ''
  const d = String(id).split(':')[0].replace(/\D/g, '')
  return d || ''
}

function FilialWhatsRow({ filial }: { filial: { id: number; nome: string } }) {
  const [abrirQr, setAbrirQr] = useState(false)
  const { data: st, refetch } = useQuery<any>({
    queryKey: ['wa-status', filial.id],
    queryFn: async () => (await api.get('/whatsapp/status', { params: { filial_id: filial.id } })).data,
    refetchInterval: abrirQr ? 2500 : false,
  })
  const status: string = st?.status || 'disconnected'
  const conectado = status === 'connected'

  useEffect(() => {
    if (conectado && abrirQr) { setAbrirQr(false); toast.success(`WhatsApp da ${filial.nome} conectado!`) }
  }, [conectado]) // eslint-disable-line

  const conectar = useMutation({
    mutationFn: async () => (await api.post('/whatsapp/conectar', null, { params: { filial_id: filial.id } })).data,
    onSuccess: () => { setAbrirQr(true); refetch() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Falha ao iniciar conexão'),
  })
  const desconectar = useMutation({
    mutationFn: async () => (await api.post('/whatsapp/desconectar', null, { params: { filial_id: filial.id } })).data,
    onSuccess: () => { setAbrirQr(false); toast.success('WhatsApp desconectado'); refetch() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })

  return (
    <div className="border border-ink-200 rounded-lg p-3">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="font-medium text-ink-900 truncate">{filial.nome}</div>
          {conectado ? (
            <div className="text-xs text-emerald-700 flex items-center gap-1">
              <CheckCircle2 size={13} /> Conectado{numeroDoMe(st?.me) ? ` · ${numeroDoMe(st?.me)}` : ''}
            </div>
          ) : (
            <div className="text-xs text-ink-500">
              {status === 'qr' ? 'Aguardando leitura do QR…' : status === 'connecting' ? 'Conectando…' : 'Desconectado'}
            </div>
          )}
        </div>
        {conectado ? (
          <button onClick={() => desconectar.mutate()} disabled={desconectar.isPending}
            className="btn-secondary !py-1.5 text-xs text-rose-700 flex items-center gap-1">
            <LogOut size={13} /> Desconectar
          </button>
        ) : (
          <button onClick={() => conectar.mutate()} disabled={conectar.isPending}
            className="btn-primary !py-1.5 text-xs flex items-center gap-1">
            {conectar.isPending ? <Loader2 size={13} className="animate-spin" /> : <Smartphone size={13} />}
            {abrirQr ? 'Atualizar QR' : 'Conectar'}
          </button>
        )}
      </div>

      {abrirQr && !conectado && (
        <div className="mt-3 flex flex-col items-center gap-2 border-t border-ink-100 pt-3">
          {st?.qr ? (
            <>
              <img src={st.qr} alt="QR Code WhatsApp" className="w-52 h-52 rounded bg-white p-1 border border-ink-200" />
              <p className="text-xs text-ink-500 text-center max-w-xs">
                No celular: <strong>WhatsApp → Configurações → Aparelhos conectados → Conectar um aparelho</strong> e aponte para o QR.
              </p>
            </>
          ) : (
            <div className="flex items-center gap-2 text-sm text-ink-500 py-6">
              <Loader2 size={16} className="animate-spin" /> Gerando QR Code…
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default function WhatsAppCard() {
  const filiais = useFilial(s => s.filiais)
  // Sessão por filial; se a empresa não tiver filiais cadastradas, cai numa "Empresa" (id 0).
  const linhas = filiais.length
    ? filiais.map(f => ({ id: f.id, nome: f.nome_fantasia || f.razao_social }))
    : [{ id: 0, nome: 'Empresa' }]

  return (
    <div className="card">
      <div className="px-5 py-4 border-b border-ink-200 flex items-center gap-2">
        <MessageCircle className="text-emerald-600" size={18} />
        <div>
          <h3 className="font-semibold">WhatsApp</h3>
          <p className="text-xs text-ink-500">Conecte o WhatsApp de cada loja para enviar Ordens de Serviço direto ao cliente.</p>
        </div>
      </div>
      <div className="p-5 space-y-2">
        {linhas.map(f => <FilialWhatsRow key={f.id} filial={f} />)}
      </div>
    </div>
  )
}
